import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import {BaseCompetitorArticleData, CompetitorArticleData} from "../../types/competitor-article-data.types";
import {defaultCompetitorArticleData} from "../../configs/default-competitor-article-data";
import {DecimalLimitDirective} from "../../../../../../../../shared/directives/decimal-limit.directive";
import {MatButton} from "@angular/material/button";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {competitorArticlesFormFields} from "../../configs/competitor-articles-form-fields";
import {CompetitorArticlesService} from "../../services/competitor-articles.service";
import {competitorArticlesResourceType} from "../../configs/competitor-articles-modal-config";
import {BaseModalService} from "../../../../../../../../core/components/modals/services/base-modal.service";
import {
  CustomMatSelectComponent
} from "../../../../../../../../shared/components/ui/custom-mat-select/custom-mat-select.component";
import {SelectData} from "../../../../../../../../core/types/records-table.types";

@Component({
  selector: 'app-competitor-articles-form',
  imports: [
    DecimalLimitDirective,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    CustomMatSelectComponent,
    ReactiveFormsModule
  ],
  templateUrl: './competitor-articles-form.component.html',
  styleUrl: './competitor-articles-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompetitorArticlesFormComponent {
  competitorBrandId = input.required<number>();
  competitorArticleForm = input.required<FormGroup>();
  competitorArticleData = input<CompetitorArticleData>(defaultCompetitorArticleData);
  noFirstData = input<boolean>(false);
  isEditModeActive = input<boolean>(false);
  productGroupSelectData = input<Array<SelectData>>([]);
  updateData = output<CompetitorArticleData>();
  deleteData = output();

  private competitorArticlesService: CompetitorArticlesService = inject(CompetitorArticlesService);
  private baseModalService: BaseModalService = inject(BaseModalService);

  public readonly competitorArticlesFormFields = competitorArticlesFormFields;
  public selectedProductGroupId = computed(() => this.competitorArticleData().productGroupId?.toString() || '');

  public getFieldClass(key: string, disabled?: boolean): string {
    if (!this.competitorArticleData().id) { return ''; }
    if (disabled) { return 'none-pointer-events'; }

    return this.isEditModeActive() ? '' : 'none-pointer-events';
  }

  public async handleValueChange(key: string, value?: string): Promise<void> {
    if (!this.competitorArticleData().id) { return; }
    if (this.competitorArticleForm().get(key)?.invalid) {
      this.competitorArticleForm().get(key)?.setValue(this.competitorArticleData()[key]);
      return;
    }

    value && this.competitorArticleForm().get(key)?.setValue(value);
    const newValue = this.competitorArticleForm().get(key)?.value;

    const params = {
      path: competitorArticlesResourceType.path,
      recordId: this.competitorArticleData().id,
      fieldName: key,
      value: newValue
    };

    const data = await this.competitorArticlesService.updateCompetitorArticleField(params)
      .catch(() => this.competitorArticleData());

    this.updateData.emit(data);
  }

  public handleDeleteData(): void {
    this.deleteData.emit();
  }

  public async handleCreateData(): Promise<void> {
    const data: BaseCompetitorArticleData = {
      competitorBrandId: this.competitorBrandId(),
      ...this.competitorArticleForm().value
    };

    await this.competitorArticlesService.createCompetitorArticleData(data)
      .then((data) => this.updateData.emit(data));
  }

  public getFieldStyleCursor(key: string): string {
    if (this.isEditModeActive()) { return ''; }

    return key === 'url' ? 'pointer' : 'default';
  }

  public handleClickLink(key: string): void {
    if (key !== 'url' || this.isEditModeActive()) { return; }

    this.baseModalService.openUrlInNewTab(key, this.competitorArticleForm());
  }

  public async handleSelectedChange(value: string): Promise<void> {
    this.competitorArticleForm().get('productGroupId')?.setValue(value);
    if (!this.competitorArticleData().id) { return; }

    await this.handleValueChange('productGroupId');
  }

  public isActiveSelectField(): boolean {
    if (!this.competitorArticleData().id) { return true; }

    return this.isEditModeActive();
  }
}
